import React from 'react';
import { SummaryWrapper } from '@deeptrue-frontend/ui';
import { useFormatMessage, useCurrency } from 'components/IntlProvider';
import { productsSlugs } from 'consts/products';
import BexioSidebar from 'components/SurveyBriefing/Sidebars/BexioProject';
import CustomerSatisfaction from 'components/SurveyBriefing/Sidebars/CustomerSatisfaction';

interface Props {
  briefingSlug?: string;
  locale?: string;
  model?: any;
  insights?: any[];
  orgCodename?: string;
}

const RenderSidebar = ({
  briefingSlug,
  locale,
  model,
  insights,
  orgCodename,
}: Props) => {
  const t = useFormatMessage();
  const formatCurrency = useCurrency();

  if (briefingSlug === productsSlugs.BEXIO_SATISFACTION)
    return <BexioSidebar model={model} />;
  else if (briefingSlug === productsSlugs.CUSTOMER_SATISFACTION) {
    return <CustomerSatisfaction model={model} />;
  }

  return (
    <SummaryWrapper
      insights={insights}
      orgCodename={orgCodename}
      model={model}
      formatCurrency={formatCurrency}
      locale={locale}
      i18n={{
        title: t('briefing.summary.totalCost', 'Total cost'),
        withoutVat: t('briefing.summary.withoutVat', 'excl. VAT'),
        credits: t('briefing.summary.credits', 'Credits'),
        pricePerResponse: t(
          'briefing.summary.pricePerResponse',
          'Price per Response'
        ),
        numberOfCustomers: t(
          'briefing.summary.customersNr',
          'Number of customers'
        ),
        numberOfCustomersSum: t(
          'briefing.summary.customersNrSum',
          'Total No. of Respondents'
        ),
        country: t('briefing.summary.country', 'Countries'),
        source: {
          title: t('source.title', 'Source'),
          deeptrue: t('source.deeptrue', 'deeptrue'),
          contacts: t('source.contacts', 'contacts'),
          both: t('source.both', 'both'),
        },
        type: {
          title: t('type.title', 'Type'),
          typeOne: t('type.typeOne', 'Users'),
          typeTwo: t('type.typeTwo', 'Users & Non-Users'),
        },
        gender: {
          title: t('gender.title', 'Gender'),
          male: t('gender.male', 'Male'),
          female: t('gender.female', 'Female'),
          both: t('gender.botj', 'Both'),
          other: t('gender.other', 'Other'),
        },
        age: t('briefing.summary.age', 'Age'),
        year: t('briefing.summary.year', 'years'),
        baseFee: t('briefing.summary.baseFee', 'Base fee'),
        dataFee: t('briefing.summary.dataFee', 'Data fee'),
        brands: t('briefing.summary.brands', 'No. of brands'),
        insights: t('briefing.summary.insights', 'Insights included'),
        attributes: t('briefing.summary.attributes', 'Attribute association'),
        behavior: t('briefing.summary.behavior', 'Behavior Change'),
        unique: t('briefing.summary.unique', 'Uniqueness'),
        relevance: t('briefing.summary.relevance', 'Relevance'),
        credibility: t('briefing.summary.credibility', 'Credibility'),
        cultural: t('briefing.summary.cultural', 'Cultural Sensitivity'),
        brand: t('briefing.summary.brand', 'Brand Fit'),
        recall: t('briefing.summary.recall', 'Advertisement Recall'),
        innovation: t('briefing.summary.innovation', 'Degree of innovation'),
        detailed: t(
          'briefing.summary.detailed',
          'Detailed customer satisfaction results'
        ),
        likes: t('briefing.summary.likes', 'Likes and dislikes'),
        benchmark: t('briefing.summary.benchmark', 'Industry benchmark'),
        individual: t(
          'briefing.summary.individual',
          'Results visible on individual level'
        ),
        basic_interviews: t(
          'briefing.summary.basic_interviews',
          '10 interviews per month included'
        ),
        aggregated: t(
          'briefing.summary.aggregated',
          'Aggregated customer satisfaction results'
        ),
        advanced_interviews: t(
          'briefing.summary.advanced_interviews',
          '30 interviews per month included'
        ),
        professional_interviews: t(
          'briefing.summary.professional_interviews',
          '100 interviews per month included'
        ),
        tooltip: {
          base_fee: t(
            'briefing.summary.tooltip.base_fee',
            'Depending on the number of countries, we charge a minimum fee.'
          ),
          data_fee: t(
            'briefing.summary.tooltip.data_fee',
            'Depending on the information included in the project setup, we charge an additional fee for data extraction.'
          ),
          credits: t(
            'briefing.summary.tooltip.credits',
            'To use the deeptrue tools, you must first purchase Credits. You can buy credits in the "Payments" section of the dashboard. Immediately after, you can enjoy all the tools!'
          ),
        },
        voucher: {
          title: t('vouchers.title', 'Do you have a voucher?'),
          code: t('vouchers.code', 'Voucher code'),
          apply: t('vouchers.apply', 'Apply'),
          remove: t('vouchers.remove', 'Remove'),
          applied: t('vouchers.applied', 'Voucher applied'),
          error: t('vouchers.error', 'Invalid code'),
        },
        brandImpact: t('briefing.summary.brandImpact', 'Brand Impact'),
        advertisingMessage: t(
          'briefing.summary.advertisingMessage',
          'Transfer of your intended message'
        ),
        audienceRelevance: t(
          'briefing.summary.audienceRelevance',
          'Relevance of audience'
        ),
        impression: t('briefing.summary.impression', 'Overall Impression'),
        willingnessParticipation: t(
          'briefing.summary.willingnessParticipation',
          'Willingness to participate again'
        ),
        willingnessRecommend: t(
          'briefing.summary.willingnessRecommend',
          'Willingness to recommend'
        ),
        openFeedback: t('briefing.summary.openFeedback', 'Any open feedback'),
        motivations: t('briefing.summary.motivations', 'Motivations'),
        networking: t(
          'briefing.summary.networking',
          'Networking opportunities'
        ),
        registration: t(
          'briefing.summary.registration',
          'Registration process'
        ),
        speakers: t('briefing.summary.speakers', 'Speakers and sessions'),
        inspiration: t(
          'briefing.summary.inspiration',
          'Inspiration for topics'
        ),
        discussion: t('briefing.summary.discussion', 'Duration for discussion'),
        duration: t('briefing.summary.duration', 'Duration of content'),
        awareness: t('briefing.summary.awareness', 'Awareness Sources'),
        platform: t('briefing.summary.platform', 'Platform experience'),
        techSupport: t('briefing.summary.techSupport', 'Tech Support'),
        thoughts: t('briefing.summary.thoughts', 'Thoughts on event'),
        expectations: t('briefing.summary.expectations', 'Event expectations'),
        benefits: t('briefing.summary.benefits', 'Benefits for sponsoring'),
        revisit: t('briefing.summary.revisit', 'Revisit'),
        informationBefore: t(
          'briefing.summary.informationBefore',
          'Information before event'
        ),
        venue: t('briefing.summary.venue', 'Venue and equipment'),
        optimization: t(
          'briefing.summary.optimization',
          'Optimization opportunities'
        ),
        acquaintances: t(
          'briefing.summary.acquaintances',
          'Acquaintances who want to speak'
        ),
        organization: t('briefing.summary.organization', 'Organisation'),
        roles: t('briefing.summary.roles', 'Roles among employees'),
        goal: t('briefing.summary.goal', 'Goal achievement'),
        businessImpact: t(
          'briefing.summary.businessImpact',
          'Impact on business goals'
        ),
        departmentImpact: t(
          'briefing.summary.departmentImpact',
          'Impact on department'
        ),
        recommend: t('briefing.summary.recommend', 'Willingness to recommend'),
        volunteeringInterest: t(
          'briefing.summary.volunteeringInterest',
          'Interest in volunteering again'
        ),
      }}
    />
  );
};

export default RenderSidebar;
